/**
 * PDF viewer modal for OncoEMR task thread attachments (`pdf_links` from messages API).
 *
 * Uses Firebase Storage signed URLs via `gcs_path`; raw `gcs_url` values are not authenticated.
 *
 * @module taskPdfViewerModal
 */
import { useEffect, useMemo, useState } from "react";
import { closeModal, Modal, openModal, SpinningLoader } from "risa-oasis-ui_v2";

import { getFileDownloadUrl } from "../../../../../api/firebase/firestoreService";
import PdfRender from "../../../../../components/pdfRender/pdfRender";
import type { OncoemrTaskThreadPdfLink } from "../../../../../data-model/oncoemrTaskMessagesModel";
import { ModalId } from "../../../../../enums/modalId";
import ChevronDown from "../../../../../svg/chevron-down";
import DownloadIcon from "../../../../../svg/download-icon";
import { logError } from "../../../../../utils/customLogger";
import "./taskPdfViewerModal.scss";

/** Props for {@link TaskPdfViewerModal}. */
interface TaskPdfViewerModalProps {
  pdfLinks: OncoemrTaskThreadPdfLink[];
  taskTitle?: string;
  onClose: () => void;
}

/**
 * Renders prev/next controls when more than one PDF is linked on the task.
 *
 * @param props - Current index, total count, and navigation handlers.
 * @returns Center section of the toolbar (filename and optional prev/next).
 */
function TaskPdfViewerNavigation({
  selectedIndex,
  totalCount,
  filename,
  onPrevious,
  onNext,
}: {
  selectedIndex: number;
  totalCount: number;
  filename: string;
  onPrevious: () => void;
  onNext: () => void;
}) {
  const showPrevious = selectedIndex > 0;
  const showNext = selectedIndex < totalCount - 1;

  if (totalCount <= 1) {
    return (
      <p className="font-lato min-w-0 flex-1 truncate text-center text-sm font-semibold text-primaryGray-2">
        {filename}
      </p>
    );
  }

  return (
    <div className="flex min-w-0 flex-1 items-center justify-center gap-3">
      <button
        type="button"
        className="flex shrink-0 items-center gap-1 text-sm font-bold text-[#06F] disabled:cursor-not-allowed disabled:opacity-40"
        onClick={onPrevious}
        disabled={!showPrevious}
      >
        <ChevronDown rotate={90} stroke="#0056D6" />
        <span>Prev</span>
      </button>
      <p className="font-lato min-w-0 truncate text-center text-sm font-semibold text-primaryGray-2">
        {filename} ({selectedIndex + 1}/{totalCount})
      </p>
      <button
        type="button"
        className="flex shrink-0 items-center gap-1 text-sm font-bold text-[#06F] disabled:cursor-not-allowed disabled:opacity-40"
        onClick={onNext}
        disabled={!showNext}
      >
        <span>Next</span>
        <ChevronDown rotate={-90} stroke="#0056D6" />
      </button>
    </div>
  );
}

/**
 * Opens the signed PDF URL in a new tab for download.
 *
 * @param signedPdfUrl - Firebase download URL for the current attachment.
 */
const downloadTaskPdf = (signedPdfUrl: string): void => {
  window.open(signedPdfUrl, "_blank", "noopener,noreferrer");
};

/**
 * Displays task PDFs in an Oasis modal by resolving `gcs_path` to a Firebase download URL.
 *
 * @param props - PDF link rows, optional task title for the modal shell, and close handler.
 * @returns Modal with embedded {@link PdfRender} and multi-document navigation.
 */
const TaskPdfViewerModal = ({
  pdfLinks,
  taskTitle,
  onClose,
}: TaskPdfViewerModalProps) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [signedPdfUrl, setSignedPdfUrl] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const selectedLink = pdfLinks[selectedIndex];
  const storagePath = selectedLink?.gcs_path?.trim() ?? "";

  const modalTitle = useMemo(() => {
    if (taskTitle?.trim()) {
      return pdfLinks.length > 1 ? `${taskTitle} — PDFs` : `${taskTitle} — PDF`;
    }
    return pdfLinks.length > 1 ? "Task PDFs" : "Task PDF";
  }, [pdfLinks.length, taskTitle]);

  useEffect(() => {
    openModal(ModalId.TASK_PDF_VIEWER_MODAL);
  }, []);

  useEffect(() => {
    if (!storagePath) {
      setSignedPdfUrl("");
      setLoadError("No storage path for this PDF.");
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const loadSignedUrl = async () => {
      setIsLoading(true);
      setLoadError(null);
      setSignedPdfUrl("");

      try {
        const downloadUrl = await getFileDownloadUrl(storagePath);
        if (cancelled) {
          return;
        }
        if (downloadUrl) {
          setSignedPdfUrl(downloadUrl);
        } else {
          setLoadError("Could not load this PDF. Please try again.");
        }
      } catch (error) {
        if (!cancelled) {
          logError(error as Error, "Failed to fetch task PDF download URL");
          setLoadError("Could not load this PDF. Please try again.");
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    void loadSignedUrl();

    return () => {
      cancelled = true;
    };
  }, [storagePath]);

  const dismiss = () => {
    closeModal(ModalId.TASK_PDF_VIEWER_MODAL, onClose);
  };

  const handlePrevious = () => {
    setSelectedIndex((index) => Math.max(0, index - 1));
  };

  const handleNext = () => {
    setSelectedIndex((index) => Math.min(pdfLinks.length - 1, index + 1));
  };

  const handleDownload = () => {
    if (signedPdfUrl) {
      downloadTaskPdf(signedPdfUrl);
    }
  };

  if (!selectedLink) {
    return null;
  }

  const canDownload = Boolean(signedPdfUrl) && !isLoading && !loadError;

  return (
    <Modal
      dialogId={ModalId.TASK_PDF_VIEWER_MODAL}
      title={modalTitle}
      saveButtonText=""
      cancelText=""
      onSave={() => {}}
      onCancel={dismiss}
      onClose={dismiss}
      hideFooter
      heightPercentage={80}
    >
      <div className="task-pdf-viewer-modal__body flex h-full min-h-0 w-full flex-col overflow-hidden">
        <div className="flex shrink-0 items-center gap-3 border-b border-primaryGray-14 px-4 py-2">
          <TaskPdfViewerNavigation
            selectedIndex={selectedIndex}
            totalCount={pdfLinks.length}
            filename={selectedLink.filename}
            onPrevious={handlePrevious}
            onNext={handleNext}
          />
          <button
            type="button"
            className="font-lato flex shrink-0 items-center gap-1 text-xs font-semibold text-[#06F] disabled:cursor-not-allowed disabled:opacity-40"
            onClick={handleDownload}
            disabled={!canDownload}
            aria-label="Download PDF"
          >
            <DownloadIcon />
            <span>Download</span>
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-hidden">
          {isLoading ? (
            <div className="flex h-full w-full items-center justify-center gap-2">
              <SpinningLoader />
              <span className="font-lato text-sm text-primaryGray-3">
                Loading PDF…
              </span>
            </div>
          ) : null}

          {!isLoading && loadError ? (
            <div className="flex h-full w-full items-center justify-center px-4">
              <p className="font-lato text-center text-sm text-red-600">
                {loadError}
              </p>
            </div>
          ) : null}

          {!isLoading && !loadError && signedPdfUrl ? (
            <PdfRender
              key={signedPdfUrl}
              file={signedPdfUrl}
              hideRotateButtons
            />
          ) : null}
        </div>
      </div>
    </Modal>
  );
};

export default TaskPdfViewerModal;
